/**
 * Bilingual ES/EN from the first line of implementation (design spec §10).
 *
 * `es-ES` is primary and is the fallback, so a key missing from the English
 * catalogue degrades to Spanish rather than to a raw key. Nothing in `src/`
 * renders a literal string; the parity test in `src/test/i18n.test.ts` fails the
 * build if the two catalogues drift apart.
 *
 * The **DeCA document itself is always Spanish** in either UI language. Its
 * section names and field labels are fixed by `DICCIONARIO_CAMPOS`, so they live
 * in `src/deca/fields.ts` as constants rather than as translatable strings — see
 * the note on `FieldDef.labelEs`.
 */

import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

import es from '../locales/es.json'
import en from '../locales/en.json'

export const SUPPORTED_LANGUAGES = ['es', 'en'] as const
export type Language = (typeof SUPPORTED_LANGUAGES)[number]

const STORAGE_KEY = 'deca.language'

export function isLanguage(value: unknown): value is Language {
  return typeof value === 'string' && (SUPPORTED_LANGUAGES as readonly string[]).includes(value)
}

/**
 * Remembered choice, otherwise Spanish.
 *
 * Deliberately **not** the browser's language. This is a Spanish product for
 * Spanish operators, `es-ES` is the primary locale (design spec §10), and the
 * sign-in screen greeting a Madrid traffic manager in English because their
 * laptop is set to en-GB would be wrong. English arrives from the user's own
 * profile after sign-in, or from the switcher — both explicit.
 */
function initialLanguage(): Language {
  const stored = typeof localStorage === 'undefined' ? null : localStorage.getItem(STORAGE_KEY)
  return isLanguage(stored) ? stored : 'es'
}

export function rememberLanguage(language: Language): void {
  localStorage.setItem(STORAGE_KEY, language)
}

/** True when the user has picked a language, so the profile's must not override. */
export function hasExplicitLanguage(): boolean {
  return isLanguage(localStorage.getItem(STORAGE_KEY))
}

void i18n.use(initReactI18next).init({
  resources: {
    es: { translation: es },
    en: { translation: en },
  },
  lng: initialLanguage(),
  fallbackLng: 'es',
  interpolation: { escapeValue: false },
  returnNull: false,
  // There is one namespace, and several keys legitimately contain a colon —
  // the API scopes are `deca:read`, `org:admin` and so on. With the default
  // separator i18next reads `scope.deca:read` as namespace `scope.deca`, key
  // `read`, and silently renders the bare word instead of the label.
  nsSeparator: false,
})

export default i18n
